/* ============================================================
   RYZE FRAGRANCES — Wishlist System (Standalone)
   ============================================================
   PURPOSE:
   Complete wishlist functionality using localStorage.
   Works independently — no third-party app dependencies.

   FEATURES:
   - Product page: the square heart toggle ("Save to wishlist", aria-pressed)
   - Collection page: Heart icon on product cards
   - Header: Badge counter on heart icon
   - Wishlist page: Grid of saved products with remove + add-to-cart
   - Toast notifications
   - Cross-tab sync via storage event

   STORAGE:
   localStorage key: 'ryze_wishlist'
   Format: JSON array of product handle strings
   Example: ["como","leen","nova"]
   ============================================================ */

(function() {
  'use strict';
  /* CODE-26 (2026-10-18): one load only; a second run would wrap fetch and
     XHR twice and double every badge read. */
  if (window.__ryzeWishlistInit) return;
  window.__ryzeWishlistInit = true;

  const STORAGE_KEY = 'ryze_wishlist';
  /* the PDP square heart's fixed (visually hidden) name; aria-pressed says whether it is saved */
  const PDP_NAME = 'Save to wishlist';

  /* CODE-09 (2026-10-18): storage may be full, blocked (private mode) or
     hold a foreign value. Reads are shape-checked (an array of strings, so
     .includes never matches a substring of a corrupt string); writes are
     guarded and report whether they held. */
  function readList() {
    try {
      var parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter(function(h) { return typeof h === 'string'; }) : [];
    } catch (e) {
      return [];
    }
  }
  function writeList(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      return true;
    } catch (e) {
      return false;
    }
  }

  /* ─── Core Storage ─── */
  const RyzeWishlist = {
    getAll() {
      return readList();
    },

    has(handle) {
      return this.getAll().includes(handle);
    },

    add(handle) {
      const items = this.getAll();
      if (!items.includes(handle)) {
        items.push(handle);
        if (!writeList(items)) return false;
        this._dispatch();
        return true;
      }
      return false;
    },

    remove(handle) {
      const items = this.getAll().filter(h => h !== handle);
      if (!writeList(items)) return false;
      this._dispatch();
      return true;
    },

    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      } else {
        return this.add(handle); // false when storage refused the write
      }
    },

    count() {
      return this.getAll().length;
    },

    clear() {
      writeList([]);
      this._dispatch();
    },

    _dispatch() {
      document.dispatchEvent(new CustomEvent('ryze:wishlist:changed', {
        detail: { items: this.getAll(), count: this.count() }
      }));
    }
  };

  /* ─── Toast ───
     ONE TOAST FAMILY (dev2.15-fix-cart, 30 Oct 2026): the wishlist answers in
     the house capsule (window.RyzeToast, assets/ryze-cart-toast.js: the
     fragrance's name, "Saved to wishlist" / "Removed from wishlist", a heart,
     View wishlist) instead of its own ink pill. The pill below stays only as
     the fallback when the house toast is absent. */
  function wishTitle(handle, card) {
    var t = card && card.getAttribute ? (card.getAttribute('data-title') || '') : '';
    if (t) return t;
    return String(handle || '').split('-').map(function (w) { return w ? w.charAt(0).toUpperCase() + w.slice(1) : w; }).join(' ');
  }
  /* the heart's fixed accessible name: "Save Como to wishlist" */
  function heartName(handle, card) {
    return 'Save ' + wishTitle(handle, card) + ' to wishlist';
  }
  function showWishToast(added, handle, card) {
    if (window.RyzeToast && typeof window.RyzeToast.show === 'function') {
      try { window.RyzeToast.show(wishTitle(handle, card), added ? 'saved' : 'unsaved'); return; } catch (e) { /* the pill below answers */ }
    }
    showToast(added ? 'Added to wishlist' : 'Removed from wishlist');
  }
  function showToast(message) {
    let el = document.getElementById('ryze-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'ryze-toast';
      el.className = 'ryze-toast';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('ryze-toast--visible');
    clearTimeout(el._timeout);
    el._timeout = setTimeout(function() {
      el.classList.remove('ryze-toast--visible');
    }, 2500);
  }

  /* ─── Header Badge ─── */
  function updateHeaderBadge() {
    const count = RyzeWishlist.count();
    const badges = document.querySelectorAll('.ryze-nav__wishlist-count');
    badges.forEach(function(badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
      // Bump animation matching cart badge
      /* Bump ONLY on a real count change, and NEVER during the first second
         after boot: navigations write every badge fresh (the marker starts
         empty on a new page), and those load-time writes must initialise
         silently. The boot anchor is shared: whichever script writes first
         plants it, so all writers count from the same moment. */
      window.__ryzeBadgeBoot = window.__ryzeBadgeBoot || Date.now();
      var ryzeBadgeMoved = badge.dataset.ryzeLastCount !== String(badge.textContent);
      badge.dataset.ryzeLastCount = String(badge.textContent);
      if (ryzeBadgeMoved && Date.now() - window.__ryzeBadgeBoot > 1000) {
        badge.classList.remove('ryze-badge-bump');
        void badge.offsetWidth;
        badge.classList.add('ryze-badge-bump');
      }
    });
  }

  /* ─── Product Page: Wishlist Button ─── */
  function initProductPageButton() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    const productHandle = window.location.pathname.split('/products/')[1];
    if (!productHandle) return;
    const handle = productHandle.split('?')[0].split('#')[0];

    // Find the buy buttons block
    const buyButtons = document.querySelector('.product-form');
    if (!buyButtons) return;

    // Check if button already exists
    if (document.querySelector('.ryze-wl-toggle')) return;

    const isAdded = RyzeWishlist.has(handle);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ryze-wl-toggle' + (isAdded ? ' ryze-wl-toggle--active' : '');
    btn.dataset.handle = handle;
    /* THE HEART IS A TOGGLE (dev2.15-calls-cart): the square heart's clipped
       name is fixed and aria-pressed carries the saved state */
    btn.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
    btn.innerHTML = heartSVG(isAdded) + '<span>' + PDP_NAME + '</span>';

    btn.addEventListener('click', function() {
      const added = RyzeWishlist.toggle(handle);
      btn.classList.toggle('ryze-wl-toggle--active', added);
      btn.innerHTML = heartSVG(added) + '<span>' + PDP_NAME + '</span>';
      btn.setAttribute('aria-pressed', added ? 'true' : 'false');
      updateHeaderBadge();
      showWishToast(added, handle, null);
    });

    /* Owner 2026-08-04: the toggle is a square heart beside the quantity
       stepper (its row container is flexed in ryze-custom.css). Dawn's
       variant re-render updates the stepper's input in place rather than
       replacing the container, so the button survives size switches. If the
       row is ever absent, the old slot above the form still works. */
    const qtyRow = document.querySelector('.product-form__quantity .price-per-item__container');
    if (qtyRow) {
      qtyRow.appendChild(btn);
    } else {
      buyButtons.parentNode.insertBefore(btn, buyButtons);
    }
  }

  function heartSVG(filled) {
    if (filled) {
      return '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>';
    }
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>';
  }

  /* ─── Collection / Home Page: Card Hearts ─── */
  function initCardHearts(scope) {
    /* .ryze-100ml-card added 2026-07-31 (owner order): the 100 ml offer cards
       carry the same top-right heart as every other product card. The li has
       no inner .card, so the default branch below appends to the card itself.
       .ryze-pcard added the same day: THE universal product card (home strip +
       all-products grid) takes the heart the same way. */
    const cards = (scope && scope.querySelectorAll ? scope : document).querySelectorAll('.card-wrapper, .ryze-product-card, .ryze-100ml-card, .ryze-pcard');
    cards.forEach(function(card) {
      // Try to find the product link to extract handle
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return;

      const href = link.getAttribute('href');
      const match = href.match(/\/products\/([^?#/]+)/);
      if (!match) return;

      const handle = match[1];

      // Check if heart already exists
      if (card.querySelector('.ryze-wl-heart')) return;

      const isAdded = RyzeWishlist.has(handle);

      const heart = document.createElement('button');
      heart.type = 'button';
      heart.className = 'ryze-wl-heart' + (isAdded ? ' ryze-wl-heart--active' : '');
      heart.dataset.handle = handle;
      /* THE HEART IS A TOGGLE (dev2.15-calls-cart, 31 Oct 2026; the sweep's
         P3-25, Sawy's 'all as recommended'): one fixed name that says what
         it does and for which fragrance, and aria-pressed carries the saved
         state (a name that flipped AND a pressed state would say it twice). */
      heart.setAttribute('aria-label', heartName(handle, card));
      heart.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
      heart.innerHTML = heartSVG(isAdded);

      heart.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const added = RyzeWishlist.toggle(handle);
        heart.classList.toggle('ryze-wl-heart--active', added);
        heart.innerHTML = heartSVG(added);
        heart.setAttribute('aria-pressed', added ? 'true' : 'false');
        updateHeaderBadge();
        showWishToast(added, handle, card);
      });

      // Append heart to the card wrapper itself (NOT inside .card__media)
      // so it escapes the z-index:0 stacking context of .card__media
      // and sits above the .card__heading a::after overlay (z-index:1)
      if (card.classList.contains('ryze-product-card')) {
        // RYZE strip cards: .ryze-product-card is an <a> tag itself
        card.style.position = 'relative';
        card.appendChild(heart);
      } else {
        // Dawn cards: append to .card or .card-wrapper
        var cardInner = card.querySelector('.card');
        var target = cardInner || card;
        target.style.position = 'relative';
        target.appendChild(heart);
      }
    });
  }

  /* ─── Wishlist Page Renderer ─── */
  /* ─── Wishlist Page: the distributor (rebuilt 2026-07-31) ───
     The catalogue is exactly five products and the page server-renders all
     five universal cards once (sections/ryze-wishlist.liquid). This just
     MOVES each card's cell between "your wishlist" and the recommendation
     grid based on localStorage: the card's own heart is the remove control,
     and removing returns the card to recommendations automatically. No
     fetching, no templating, and every card keeps full toggle/qty/add/buy
     behavior because ryze-pcard.js delegates from the document. */
  function initWishlistPage() {
    const wlGrid = document.getElementById('ryze-wl-grid');
    if (!wlGrid) return; // Not on wishlist page
    const empty = document.getElementById('ryze-wl-empty');
    const countEl = document.getElementById('ryze-wl-count');
    const recoWrap = document.getElementById('ryze-wl-reco');
    const recoGrid = document.getElementById('ryze-wl-reco-grid');
    const recoTitle = document.getElementById('ryze-wl-reco-title');
    if (!recoGrid) return;

    const distribute = function () {
      const wished = RyzeWishlist.getAll();
      const cells = Array.prototype.slice.call(document.querySelectorAll('[data-wl-cell]'));
      cells.sort(function (a, b) {
        return Number(a.getAttribute('data-wl-order')) - Number(b.getAttribute('data-wl-order'));
      });
      let wlCount = 0;
      cells.forEach(function (cell) {
        if (wished.indexOf(cell.getAttribute('data-wl-handle')) !== -1) {
          wlGrid.appendChild(cell);
          wlCount += 1;
        } else {
          recoGrid.appendChild(cell);
        }
      });
      wlGrid.style.display = wlCount ? '' : 'none';
      if (empty) empty.style.display = wlCount ? 'none' : '';
      if (countEl) countEl.textContent = wlCount ? wlCount + (wlCount === 1 ? ' fragrance saved' : ' fragrances saved') : '';
      if (recoTitle) recoTitle.textContent = wlCount ? 'Complete your collection' : 'Curated for you';
      if (recoWrap) recoWrap.style.display = cells.length - wlCount ? '' : 'none';
    };
    distribute();

    // A heart tap anywhere on this page reshuffles the two grids a beat
    // later. CAPTURE phase on purpose: the heart's own handler calls
    // stopPropagation(), so a bubble listener at the document never hears
    // it (shipped once: storage toggled, the grids never moved).
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.ryze-wl-heart')) {
        setTimeout(distribute, 60);
      }
    }, true);
    // Another tab edited the wishlist.
    window.addEventListener('storage', function (e) {
      if (e && e.key === 'ryze_wishlist') distribute();
    });
  }

  /* ─── Shopify Root (module-level) ─── */
  var root = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes.root : '/';

  /* ─── Global Cart Badge Sync ─── */
  // Intercept all fetch calls to Shopify cart endpoints so the
  // header badge stays current on every page (cart, product, etc.)
  /* CODE-08 (2026-10-18): ONE coalesced /cart.js read per burst of cart
     writes (it was one read per write, feeding the 429 pressure the promo
     treats as routine), and every derived promise is caught (a failed cart
     request used to raise an unhandled rejection from this shim). The
     caller still gets the original promise, untouched. */
  var _nativeFetch = window.fetch;
  var badgeTimer = null;
  function isCartMutationUrl(url) {
    url = String(url || '');
    return url.indexOf('/cart/add') !== -1 ||
           url.indexOf('/cart/change') !== -1 ||
           url.indexOf('/cart/update') !== -1 ||
           url.indexOf('/cart/clear') !== -1;
  }
  function paintBadges(cart) {
    var badges = document.querySelectorAll('.ryze-nav__cart-count');
    badges.forEach(function(b) {
      b.textContent = cart.item_count;
      b.style.display = cart.item_count > 0 ? 'flex' : 'none';
      /* Bump ONLY on a real count change, and NEVER during the first second
         after boot: navigations write every badge fresh (the marker starts
         empty on a new page), and those load-time writes must initialise
         silently. The boot anchor is shared: whichever script writes first
         plants it, so all writers count from the same moment. */
      window.__ryzeBadgeBoot = window.__ryzeBadgeBoot || Date.now();
      var ryzeBadgeMoved = b.dataset.ryzeLastCount !== String(b.textContent);
      b.dataset.ryzeLastCount = String(b.textContent);
      if (ryzeBadgeMoved && Date.now() - window.__ryzeBadgeBoot > 1000) {
        b.classList.remove('ryze-badge-bump');
        void b.offsetWidth;
        b.classList.add('ryze-badge-bump');
      }
    });
  }
  function scheduleBadgeRead() {
    // Small delay to let Shopify finish processing; later writes restart it.
    clearTimeout(badgeTimer);
    badgeTimer = setTimeout(function() {
      _nativeFetch.call(window, root + 'cart.js')
        .then(function(r) {
          if (!r.ok) throw new Error('cart.js ' + r.status);
          return r.json();
        })
        .then(paintBadges)
        .catch(function() {});
    }, 300);
  }

  window.fetch = function() {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url ? arguments[0].url : '');
    var result = _nativeFetch.apply(this, arguments);
    if (isCartMutationUrl(url)) {
      result.then(scheduleBadgeRead, function() {});
    }
    return result;
  };

  // Same for XMLHttpRequest (some Shopify themes use it)
  var _xhrOpen = XMLHttpRequest.prototype.open;
  var _xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function(method, url) {
    this._ryzeCartUrl = url;
    return _xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function() {
    if (isCartMutationUrl(this._ryzeCartUrl)) {
      this.addEventListener('load', scheduleBadgeRead);
    }
    return _xhrSend.apply(this, arguments);
  };

  /* ─── Universal heart sync (owner-caught bug, 2026-07-31 Part8) ───
     Hearts used to be painted once at injection and only ever repainted
     by their OWN click handler, so the same product's heart on another
     card stayed stale (he removed all five on the wishlist page and the
     100 ml offer cards still showed them filled), and a bfcache
     back-navigation restored a frozen page nothing re-synced. Every
     heart now re-derives from localStorage on every wishlist change,
     on the cross-tab storage event, and on pageshow(persisted).
     Early-returns when the state already matches, so the card that was
     clicked is not repainted twice. */
  function syncHeart(heart) {
    var handle = heart.dataset.handle;
    if (!handle) return;
    var isAdded = RyzeWishlist.has(handle);
    var pressed = isAdded ? 'true' : 'false';
    if (heart.classList.contains('ryze-wl-heart--active') === isAdded && heart.getAttribute('aria-pressed') === pressed) return;
    heart.classList.toggle('ryze-wl-heart--active', isAdded);
    heart.innerHTML = heartSVG(isAdded);
    heart.setAttribute('aria-pressed', pressed);
  }

  function syncAllHearts() {
    document.querySelectorAll('.ryze-wl-heart').forEach(syncHeart);
    var btn = document.querySelector('.ryze-wl-toggle');
    if (btn && btn.dataset.handle) {
      var added = RyzeWishlist.has(btn.dataset.handle);
      if (btn.classList.contains('ryze-wl-toggle--active') !== added || btn.getAttribute('aria-pressed') !== (added ? 'true' : 'false')) {
        btn.classList.toggle('ryze-wl-toggle--active', added);
        btn.innerHTML = heartSVG(added) + '<span>' + PDP_NAME + '</span>';
        btn.setAttribute('aria-pressed', added ? 'true' : 'false');
      }
    }
  }

  /* ─── Cross-tab Sync ─── */
  window.addEventListener('storage', function(e) {
    if (e.key === STORAGE_KEY) {
      updateHeaderBadge();
      syncAllHearts();
    }
  });

  /* ─── Listen for wishlist changes (any heart, any surface, this page) ─── */
  document.addEventListener('ryze:wishlist:changed', function() {
    updateHeaderBadge();
    syncAllHearts();
  });

  /* ─── bfcache: iOS back-navigation restores the frozen DOM ─── */
  window.addEventListener('pageshow', function(e) {
    if (e && e.persisted) {
      updateHeaderBadge();
      syncAllHearts();
    }
  });

  /* ─── Re-inject hearts after AJAX content updates (collection filters, pagination) ─── */
  function observeDynamicCards() {
    /* cart-items covers BOTH of the cart page's swap surfaces
       (#main-cart-items .js-contents and the re-inserted cross-sell):
       the v2 controller replaces those nodes wholesale on every cart
       change, which silently threw away the client-appended hearts
       (owner-caught 2026-08-03). The host element itself is stable, so
       the observer survives every swap. */
    var targets = document.querySelectorAll('#ProductGridContainer, #product-grid, #SearchResultsProductGrid, product-recommendations, cart-items');
    targets.forEach(function(target) {
      var observer = new MutationObserver(function() {
        // Small delay to let DOM settle after innerHTML replacement
        setTimeout(initCardHearts, 50);
      });
      observer.observe(target, { childList: true, subtree: true });
    });

    // Also observe body for late-appearing product-recommendations elements
    // (they may not exist in DOM when this function first runs)
    var bodyObserver = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        for (var j = 0; j < mutations[i].addedNodes.length; j++) {
          var node = mutations[i].addedNodes[j];
          if (node.nodeType === 1 && (
            node.tagName === 'PRODUCT-RECOMMENDATIONS' ||
            (node.querySelector && node.querySelector('product-recommendations'))
          )) {
            setTimeout(initCardHearts, 100);
            return;
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ─── Init ─── */
  function init() {
    updateHeaderBadge();
    initProductPageButton();
    initCardHearts();
    initWishlistPage();
    observeDynamicCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* dev2.15-search (2026-10-30): cards a script inserts after this file's init (the search sheet's TAILORED FOR YOU,
     assets/ryze-sheet.js) take the same heart through one call: RyzeWishlist.hydrate(root). Idempotent (a card that has
     its heart is skipped), the heart and its click are exactly initCardHearts' own. */
  RyzeWishlist.hydrate = function (root) { initCardHearts(root); };

  // Expose globally for debugging
  window.RyzeWishlist = RyzeWishlist;

})();